// АДРЕС ШАГА — грамматика `#s3/2/b` и счётчик хрома `NN / 07`.
//
// ОТКУДА ПРИЕХАЛО. Грамматику написал стенд истории G-T3 (`tests/harness/history.js`), потому что
// роутера тогда не существовало, а проверять «назад» на чём-то было нужно. Здесь она становится
// источником: продакшен не имеет права импортировать из `tests/`, поэтому направление ровно такое
// — `src/` объявляет, стенд реэкспортирует.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ МОДУЛЬ. Адрес — единственная форма, в которой позиция визарда живёт снаружи
// (в адресной строке и в записи истории). Внутри позиция живёт полем `step` конверта. Две истины
// о позиции — это классический способ разъехаться, поэтому перевод между ними собран в одном
// месте и проверяется отдельно от роутера: `parseAddress` ⟷ `formatAddress`.
//
// ⚠️ АДРЕС — НЕ УРОВЕНЬ ДОСТУПА. Разобранный `#s5` означает «строка синтаксически годная», а не
// «на пятый шаг пускать». Пускать или нет решает оболочка по конверту.

/** Шагов в визарде семь (`s447.d3`); счётчик в хроме — `NN / 07`. */
export const STEP_COUNT = 7;

/**
 * Разобрать hash-адрес до гранулярности `#s3/2/b` (хром §4, `wizard-browser-rules.md`).
 *
 * `#s2` → шаг · `#s3/2` → шаг и карточка · `#s3/2/b` → шаг, карточка, подэкран.
 * Мусор и адрес вне диапазона шагов → `null`: у вызывающего одна ветка «адреса нет», а не три.
 *
 * @returns {{step: number, card: number|null, sub: string|null}|null}
 */
export function parseAddress(hash) {
  if (typeof hash !== 'string') return null;
  const m = /^#?s(\d+)(?:\/(\d+)(?:\/([a-z]))?)?$/.exec(hash.trim());
  if (!m) return null;

  const step = Number(m[1]);
  if (!Number.isInteger(step) || step < 1 || step > STEP_COUNT) return null;

  const card = m[2] === undefined ? null : Number(m[2]);
  if (card !== null && card < 1) return null;

  return { step, card, sub: m[3] ?? null };
}

/** Собрать адрес обратно. Пропуск карточки при заданном подэкране — ошибка, а не тихий `#s3//b`. */
export function formatAddress({ step, card = null, sub = null } = {}) {
  if (!Number.isInteger(step) || step < 1 || step > STEP_COUNT) {
    throw new Error(`адрес требует шаг 1..${STEP_COUNT}, получено: ${step}`);
  }
  if (sub !== null && card === null) {
    throw new Error('подэкран без карточки: адрес #s3//b не существует');
  }
  let out = `#s${step}`;
  if (card !== null) out += `/${card}`;
  if (sub !== null) out += `/${sub}`;
  return out;
}

/**
 * Форма адреса для конверта — без решётки (`s3/2/b`).
 *
 * Две формы одного адреса живут по разные стороны границы намеренно: в адресной строке решётка
 * обязательна (без неё `pushState` меняет путь, а не хэш), а в поле `step` она была бы мусором,
 * который однажды уедет в JSON и начнёт сравниваться с бесхэшевым близнецом.
 */
export function bareAddress(parsed) {
  return formatAddress(parsed).slice(1);
}

/**
 * Номер шага из адреса: `s3/2/b` → 3. Не разобрался — 0, то есть «дальше первого не ушли».
 *
 * Мягче `parseAddress`: здесь нужен ТОЛЬКО номер, и `hasWork` обязан отвечать даже на конверт,
 * записанный будущей версией с более длинным адресом. Строгий разбор — на входе в роутер.
 */
export function stepNumber(address) {
  if (typeof address !== 'string') return 0;
  const m = /^#?s(\d+)/.exec(address.trim());
  return m ? Number(m[1]) : 0;
}

/**
 * Счётчик хрома: `01 / 07` (борд шага 1, `wizard-browser-rules.md:117`).
 * Двузначный с ведущим нулём — так нарисовано; знаменатель семь, а не десять.
 */
export function stepCounter(address) {
  const n = stepNumber(address);
  if (n < 1 || n > STEP_COUNT) {
    throw new Error(`счётчик требует шаг 1..${STEP_COUNT}, получено: ${address}`);
  }
  return `${String(n).padStart(2, '0')} / ${String(STEP_COUNT).padStart(2, '0')}`;
}
